/**
 * DENET — header behaviour
 * Mega menu (hover + keyboard), scroll state, mobile drawer, and the triggers
 * that hand off to the search overlay and cart drawer.
 */

import { DenetElement, define, qsa, rafThrottle, media, EVENTS, emit, lockScroll, unlockScroll } from '@denet/core';

const OPEN_DELAY = 90;
const CLOSE_DELAY = 180;

class DnHeader extends DenetElement {
  /** @type {HTMLElement | null} */
  #openItem = null;
  /** @type {number | undefined} */
  #openTimer;
  /** @type {number | undefined} */
  #closeTimer;

  mounted() {
    this.#initHeaderHeight();
    this.#initScrollState();
    this.#initMegaMenu();
    this.#initDrawer();
    this.#initTriggers();
  }

  unmounted() {
    window.clearTimeout(this.#openTimer);
    window.clearTimeout(this.#closeTimer);
  }

  /* --------------------------------------------------------- yükseklik */
  /**
   * Ölçülen değer AYRI bir değişkene yazılıyor: --dn-header-height başlığın
   * kendi iç yüksekliğini de kuruyor (.dn-header__inner min-height), oraya
   * ölçümü geri yazmak başlığı her karede büyüten bir döngü açıyordu.
   *
   * --dn-header-total başlığın hızlı arama çubuğu dahil gerçek yüksekliği.
   * Koleksiyon araç çubuğu ("24/53 ürün · Sırala") buna göre yapışıyor;
   * eskiden sabit 84 pikseli kullandığı için başlığın altında kalıyordu.
   * Kaydırınca çipler kapanıp yükseklik değişiyor, ölçüm onu izliyor.
   */
  #initHeaderHeight() {
    const wrapper = this.closest('.dn-header-sticky') || this;

    const write = rafThrottle(() => {
      const h = Math.round(wrapper.getBoundingClientRect().height);
      if (h > 0) document.documentElement.style.setProperty('--dn-header-total', `${h}px`);
    });

    write();
    window.addEventListener('scroll', write, { passive: true, signal: this.signal });
    window.addEventListener('resize', write, { passive: true, signal: this.signal });

    /* Çipler kapanınca yükseklik geçişle değişiyor; sonucu geçiş bitince de
       bir kez daha okuyoruz, aksi hâlde ara değerde kalıyordu. */
    this.addEventListener('transitionend', write, { signal: this.signal });
  }

  /* ------------------------------------------------------------- scrolled */
  #initScrollState() {
    const update = rafThrottle(() => {
      this.classList.toggle('is-scrolled', window.scrollY > 8);

      /* Sabit başlık, akışta duran duyuru şeridinin altından başlar ve şerit
         kayıp gidince tepeye oturur. */
      if (this.classList.contains('dn-header--overlay')) {
        const bar = document.querySelector('.dn-announcement');
        const barHeight = bar ? bar.offsetHeight : 0;
        const offset = Math.max(0, barHeight - window.scrollY);
        this.style.setProperty('--dn-header-top', `${offset}px`);
      }
    });
    update();
    window.addEventListener('scroll', update, { passive: true, signal: this.signal });
  }

  /* ------------------------------------------------------------ mega menu */
  #initMegaMenu() {
    const items = qsa('[data-ref="navItem"]', this);
    if (items.length === 0) return;

    for (const item of items) {
      if (!(item instanceof HTMLElement)) continue;

      if (media.pointerFine) {
        item.addEventListener('pointerenter', () => this.#scheduleOpen(item), { signal: this.signal });
        item.addEventListener('pointerleave', () => this.#scheduleClose(), { signal: this.signal });
      }

      const trigger = item.querySelector('[data-ref="navTrigger"]');
      trigger?.addEventListener(
        'click',
        (event) => {
          // On touch the first tap opens the panel instead of navigating.
          if (media.pointerFine) return;
          if (this.#openItem !== item) {
            event.preventDefault();
            this.#open(item);
          }
        },
        { signal: this.signal }
      );

      // Keyboard: opening on focus keeps the panel reachable by Tab alone.
      item.addEventListener('focusin', () => this.#open(item), { signal: this.signal });
      item.addEventListener(
        'focusout',
        (event) => {
          const next = /** @type {HTMLElement | null} */ (event.relatedTarget);
          if (!next || !item.contains(next)) this.#close(item);
        },
        { signal: this.signal }
      );
    }

    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Escape' && this.#openItem) {
          const item = this.#openItem;
          this.#close(item);
          /** @type {HTMLElement | null} */ (item.querySelector('[data-ref="navTrigger"]'))?.focus();
        }
      },
      { signal: this.signal }
    );
  }

  /** @param {HTMLElement} item */
  #scheduleOpen(item) {
    window.clearTimeout(this.#closeTimer);
    window.clearTimeout(this.#openTimer);
    this.#openTimer = window.setTimeout(() => this.#open(item), OPEN_DELAY);
  }

  #scheduleClose() {
    window.clearTimeout(this.#openTimer);
    window.clearTimeout(this.#closeTimer);
    this.#closeTimer = window.setTimeout(() => {
      if (this.#openItem) this.#close(this.#openItem);
    }, CLOSE_DELAY);
  }

  /** @param {HTMLElement} item */
  #open(item) {
    if (!item.querySelector('[data-ref="megaPanel"]')) return;
    if (this.#openItem && this.#openItem !== item) this.#close(this.#openItem);
    this.#openItem = item;
    item.classList.add('is-open');
    item.querySelector('[data-ref="navTrigger"]')?.setAttribute('aria-expanded', 'true');
  }

  /** @param {HTMLElement} item */
  #close(item) {
    item.classList.remove('is-open');
    item.querySelector('[data-ref="navTrigger"]')?.setAttribute('aria-expanded', 'false');
    if (this.#openItem === item) this.#openItem = null;
  }

  /* ---------------------------------------------------------- mobile drawer */
  #initDrawer() {
    const burger = this.ref('burger');
    const drawer = /** @type {HTMLDialogElement | null} */ (document.getElementById('DnMenuDrawer'));
    if (!burger || !drawer) return;

    const open = () => {
      drawer.showModal();
      lockScroll();
      burger.setAttribute('aria-expanded', 'true');
    };

    const close = () => drawer.close();

    burger.addEventListener('click', open, { signal: this.signal });
    drawer.querySelector('[data-ref="drawerClose"]')?.addEventListener('click', close, { signal: this.signal });

    drawer.addEventListener(
      'close',
      () => {
        unlockScroll();
        burger.setAttribute('aria-expanded', 'false');
      },
      { signal: this.signal }
    );

    // Clicking the backdrop closes: the dialog element itself is the backdrop
    // target, its children are not.
    drawer.addEventListener(
      'click',
      (event) => {
        if (event.target === drawer) close();
      },
      { signal: this.signal }
    );

    for (const toggle of qsa('[data-ref="mnavToggle"]', drawer)) {
      toggle.addEventListener(
        'click',
        () => {
          const expanded = toggle.getAttribute('aria-expanded') === 'true';
          const panelId = toggle.getAttribute('aria-controls');
          const panel = panelId ? document.getElementById(panelId) : null;
          toggle.setAttribute('aria-expanded', String(!expanded));
          panel?.setAttribute('data-open', String(!expanded));
        },
        { signal: this.signal }
      );
    }
  }

  /* -------------------------------------------------------------- triggers */
  #initTriggers() {
    /* Aramayı açan iki öge var: masaüstündeki alan ve dar ekrandaki büyüteç
       ikonu. `ref()` querySelector kullanıyor, yani yalnız ilkini buluyordu —
       ikon hiçbir zaman dinleyici almıyor, tıklayınca hiçbir şey olmuyordu.
       İkisi de aynı katmanı açıyor, ikisine de bağlanıyoruz. */
    for (const trigger of qsa('[data-ref="searchTrigger"]', this)) {
      trigger.addEventListener('click', () => emit(EVENTS.searchOpen), { signal: this.signal });
    }

    this.ref('cartTrigger')?.addEventListener(
      'click',
      (event) => {
        // The link stays a real link: if the drawer is not on the page the
        // browser simply navigates to /cart.
        if (!document.querySelector('dn-cart-drawer')) return;
        event.preventDefault();
        emit(EVENTS.drawerOpen, { drawer: 'cart' });
      },
      { signal: this.signal }
    );
  }
}

define('dn-header', DnHeader);
